import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Nav from '@/components/Nav';
import { Send, Sparkles, Lock, Crown, Zap, CheckCircle, Building2, Car, MessageCircle, Heart, Settings, LogOut, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Concierge unlock threshold (AED)
const BUDGET_THRESHOLD = 5000000; // 5M AED minimum

// Allowed subscription tiers for concierge access
const ALLOWED_TIERS = ['premium', 'elite', 'admin'];

const ConciergePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const messagesEndRef = useRef(null);
  
  // Qualification form state
  const [qualificationForm, setQualificationForm] = useState({
    budget_range: '',
    purchase_timeline: '',
    interest_areas: [],
    phone: ''
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check user profile and concierge access on mount
  useEffect(() => {
    checkConciergeAccess();
  }, []);

  const checkConciergeAccess = async () => {
    try {
      
      
      // First, get subscription status
      const subResponse = await axios.get(`${API}/subscription/status`, {
        withCredentials: true
      }).catch(() => ({ data: { tier: 'free' } }));
      
      const userTier = subResponse.data?.tier || user?.subscription_tier || 'free';
      setSubscriptionTier(userTier);
      
      // Check if user has premium/elite tier or is admin
      const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || subResponse.data?.is_admin;
      const hasPremiumAccess = isAdmin || ALLOWED_TIERS.includes(userTier.toLowerCase());
      
      if (!hasPremiumAccess) {
        // Free users don't have access - show upgrade prompt
        setIsUnlocked(false);
        setCheckingAccess(false);
        return;
      }
      
      // Premium/Elite users - check profile and concierge status
      const response = await axios.get(`${API}/onboarding/profile`, {
        withCredentials: true
      });
      
      setProfile(response.data);
      
      // Check if user qualifies for concierge access (budget or already unlocked)
      const budgetMin = response.data?.home_preferences?.budget_min || 0;
      const conciergeUnlocked = response.data?.concierge_unlocked || false;
      
      if (budgetMin >= BUDGET_THRESHOLD || conciergeUnlocked) {
        setIsUnlocked(true);
        // Initialize welcome message
        setMessages([{
          role: 'assistant',
          content: `Welcome to ASTIIV BASGAT Elite Concierge, ${user?.name || 'there'}! As a ${userTier.charAt(0).toUpperCase() + userTier.slice(1)} member, you have exclusive access to our AI-powered luxury advisor. How can I help you design your dream life today?`,
          timestamp: new Date().toISOString(),
        }]);
      } else {
        setIsUnlocked(false);
      }
    } catch (error) {
      setIsUnlocked(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleUnlockSubmit = async () => {
    if (!qualificationForm.budget_range || !qualificationForm.purchase_timeline) {
      toast.error('Please complete the qualification form');
      return;
    }
    
    try {
      
      
      // Update user profile with concierge unlock
      await axios.post(`${API}/onboarding/profile`, {
        ...profile,
        concierge_unlocked: true,
        concierge_qualification: qualificationForm
      }, {
        withCredentials: true
      });
      
      // Trigger lead intelligence update
      await axios.post(`${API}/concierge/request-access`, {
        user_id: user.id,
        qualification: qualificationForm
      }, {
        withCredentials: true
      }).catch(() => {}); // Non-critical
      
      setIsUnlocked(true);
      setShowUnlockModal(false);
      setMessages([{
        role: 'assistant',
        content: `Welcome to ASTIIV BASGAT Elite Concierge, ${user?.name || 'there'}! Thank you for qualifying. I'm your personal luxury lifestyle advisor - let's design your dream portfolio together.`,
        timestamp: new Date().toISOString(),
      }]);
      toast.success('Concierge access unlocked!');
    } catch (error) {
      toast.error('Failed to unlock concierge access');
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/concierge/chat`, {
        message: input,
        user_id: user.id,
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: response.data.timestamp,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast.error('Failed to get response from concierge');
      const errorMessage = {
        role: 'assistant',
        content: 'I apologize, but I\'m having trouble responding right now. Please try again in a moment.',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="w-12 h-12 text-gold mx-auto animate-pulse mb-4" />
          <p className="text-text-secondary">Checking access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-text-primary flex flex-col mobile-pb">
      {/* Navigation */}
      <Nav showBack activePage="/concierge" />

      {/* Unlock Modal */}
      <AnimatePresence>
        {showUnlockModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowUnlockModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-obsidian border border-white/20 rounded-2xl max-w-lg w-full p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-8 h-8 text-gold" />
                </div>
                <h2 className="font-playfair text-2xl font-semibold mb-2">Unlock Elite Concierge</h2>
                <p className="text-text-secondary text-sm">
                  Complete this short qualification to access personalized luxury assistance
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-text-secondary mb-2 block">Investment Budget Range *</label>
                  <Select 
                    value={qualificationForm.budget_range}
                    onValueChange={(value) => setQualificationForm({...qualificationForm, budget_range: value})}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="Select your budget range" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="5-10M" className="text-gray-900">AED 5M - 10M</SelectItem>
                      <SelectItem value="10-25M" className="text-gray-900">AED 10M - 25M</SelectItem>
                      <SelectItem value="25-50M" className="text-gray-900">AED 25M - 50M</SelectItem>
                      <SelectItem value="50M+" className="text-gray-900">AED 50M+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm text-text-secondary mb-2 block">Purchase Timeline *</label>
                  <Select 
                    value={qualificationForm.purchase_timeline}
                    onValueChange={(value) => setQualificationForm({...qualificationForm, purchase_timeline: value})}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="When do you plan to invest?" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="immediate" className="text-gray-900">Immediately</SelectItem>
                      <SelectItem value="1-3months" className="text-gray-900">1-3 Months</SelectItem>
                      <SelectItem value="3-6months" className="text-gray-900">3-6 Months</SelectItem>
                      <SelectItem value="6-12months" className="text-gray-900">6-12 Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm text-text-secondary mb-2 block">Phone Number (Optional)</label>
                  <Input
                    value={qualificationForm.phone}
                    onChange={(e) => setQualificationForm({...qualificationForm, phone: e.target.value})}
                    placeholder="+971 XX XXX XXXX"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
              
              <div className="flex gap-4 mt-8">
                <Button 
                  variant="outline" 
                  onClick={() => setShowUnlockModal(false)}
                  className="flex-1 border-white/20"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleUnlockSubmit}
                  className="flex-1 bg-gold text-black hover:bg-gold-light"
                >
                  <Zap className="w-4 h-4 mr-2" /> Unlock Access
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      {!isUnlocked ? (
        /* Locked State */
        <div className="flex-1 flex items-center justify-center p-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl text-center"
          >
            <div className="w-24 h-24 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-8">
              <Lock className="w-12 h-12 text-gold" />
            </div>
            <h1 className="font-playfair text-4xl font-semibold mb-4">
              Elite Concierge <span className="text-gold">Access</span>
            </h1>
            <p className="text-text-secondary text-lg mb-8 leading-relaxed">
              Our AI-powered Elite Concierge provides personalized luxury lifestyle guidance. 
              {!ALLOWED_TIERS.includes(subscriptionTier.toLowerCase()) ? (
                <> This exclusive service is available only for <span className="text-gold font-semibold">Premium</span> and <span className="text-gold font-semibold">Elite</span> members.</>
              ) : (
                <> Unlock exclusive access to get curated property recommendations, investment insights, and priority assistance from our luxury specialists.</>
              )}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              <div className="glass-card p-6 text-center">
                <Sparkles className="w-8 h-8 text-gold mx-auto mb-3" />
                <h3 className="font-semibold mb-2">AI Advisor</h3>
                <p className="text-text-secondary text-sm">Personalized recommendations</p>
              </div>
              <div className="glass-card p-6 text-center">
                <Crown className="w-8 h-8 text-gold mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Priority Access</h3>
                <p className="text-text-secondary text-sm">Off-market opportunities</p>
              </div>
              <div className="glass-card p-6 text-center">
                <Zap className="w-8 h-8 text-gold mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Fast Response</h3>
                <p className="text-text-secondary text-sm">Partner follow-up in 24h</p>
              </div>
            </div>
            
            {!ALLOWED_TIERS.includes(subscriptionTier.toLowerCase()) ? (
              /* Free tier users - Show upgrade CTA */
              <>
                <div className="glass-card p-6 mb-6 border border-gold/30">
                  <p className="text-text-secondary mb-4">
                    Your current plan: <span className="text-white font-semibold capitalize">{subscriptionTier}</span>
                  </p>
                  <p className="text-sm text-text-muted mb-4">
                    Upgrade to Premium or Elite to unlock the AI Concierge and other exclusive features.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/subscription')}
                  className="bg-gold text-black hover:bg-gold-light px-10 py-6 text-lg font-semibold"
                  data-testid="upgrade-subscription-btn"
                >
                  <Crown className="w-5 h-5 mr-2" /> Upgrade Your Plan
                </Button>
              </>
            ) : (
              /* Premium/Elite users - Show unlock CTA */
              <>
                <Button
                  onClick={() => setShowUnlockModal(true)}
                  className="bg-gold text-black hover:bg-gold-light px-10 py-6 text-lg font-semibold"
                  data-testid="unlock-concierge-btn"
                >
                  <Lock className="w-5 h-5 mr-2" /> Unlock Concierge Assistance
                </Button>
                <p className="text-text-muted text-xs mt-6">
                  Available for qualified investors with AED 5M+ budget
                </p>
              </>
            )}
          </motion.div>
        </div>
      ) : (
        /* Unlocked State - Show Chat */
        <div className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-8 py-8 flex flex-col">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 text-center"
          >
            <div className="inline-flex items-center gap-3 glass-card px-6 py-3 mb-4">
              <Crown className="w-6 h-6 text-gold" />
              <h1 className="font-playfair text-2xl font-semibold">Elite Concierge</h1>
              <span className="px-2 py-0.5 bg-gold/20 rounded-full text-xs text-gold">Unlocked</span>
            </div>
            <p className="text-text-secondary">Your personal AI luxury lifestyle advisor</p>
          </motion.div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto mb-6 space-y-6" data-testid="chat-messages-container" data-lenis-prevent>
            {messages.map((message, index) => (
              <motion.div
                key={message.timestamp || `msg-${index}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`chat-message-${index}`}
              >
                <div className={`max-w-[70%] ${
                  message.role === 'user' 
                    ? 'bg-gold text-black' 
                    : 'glass-card'
                } p-6 rounded-lg`}>
                  {message.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-3 text-gold">
                      <Crown className="w-4 h-4" />
                      <span className="text-sm font-semibold">Elite Concierge</span>
                    </div>
                  )}
                  <p className={`leading-relaxed whitespace-pre-wrap ${
                    message.role === 'user' ? 'text-black' : 'text-text-primary'
                  }`}>
                    {message.content}
                  </p>
                </div>
              </motion.div>
            ))}
            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="glass-card p-6 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-gold animate-pulse" />
                    <span className="text-text-secondary">Thinking...</span>
                  </div>
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="glass-card p-6">
            <div className="flex gap-4">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about properties, lifestyle, investments..."
                disabled={loading}
                className="flex-1 bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4 text-lg"
                data-testid="chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={loading || !input.trim()}
                className="bg-gold text-black hover:bg-gold-light transition-all duration-300 px-8"
                data-testid="chat-send-btn"
              >
                <Send className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConciergePage;